import { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, Inbox, Filter, Trash2, ChevronLeft, ChevronRight, Search, X, MapPin, Download, Wifi, WifiOff, MoreHorizontal, Check } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from '@meno/ui/components/ui/dropdown-menu'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@meno/ui/components/ui/select'
import { cn } from '@meno/ui/lib/utils'
import { getScreens, exportTasks, type Task, type TaskType } from '@meno/ui/lib/api'
import { useBranchFilter } from '@meno/ui/contexts/BranchFilterContext'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import { useIsMobile } from '@meno/ui/hooks/use-mobile'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@meno/ui/components/ui/sheet'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { useTaskCenter, isUploadTask } from '@/contexts/TaskCenterContext'
import { RunningRow, TaskRow } from '@/components/TaskCenter/taskRows'
import EmptyState from '@/components/EmptyState'

// Status buckets used by the filter — the four canonical states an operator
// reasons in, plus All. Running = actively delivering; Pending = stalled,
// waiting for an offline screen to reconnect; Completed = done; Failed = the
// success condition can no longer be met.
type StatusFilter = 'all' | 'running' | 'pending' | 'done' | 'failed'
// Type buckets fold the many task types into the things operators think in:
// publishes, uploads, changes (archive/restore/rename), license lifecycle,
// connectivity (status), everything.
type TypeFilter = 'all' | 'publish' | 'upload' | 'change' | 'license' | 'status'

// How many activity rows show per page before the pager kicks in.
const PAGE_SIZE = 12

const PUBLISH_TYPES = new Set<TaskType>([
  'publish_playlist', 'publish_layout', 'publish_media', 'publish_menu', 'publish_audio',
  'publish_audio_playlist', 'publish_stop_screen', 'publish_transition', 'content_expire',
])
const CHANGE_TYPES = new Set<TaskType>(['archive', 'restore', 'rename'])
const LICENSE_TYPES = new Set<TaskType>(['license_granted', 'license_renewed', 'license_expired', 'license_freed'])
// Connectivity churn — hidden by default behind the "Show status changes"
// toggle so it doesn't drown the feed.
const STATUS_TYPES = new Set<TaskType>(['screen_online', 'screen_offline'])

function matchesStatus(task: Task, f: StatusFilter): boolean {
  if (f === 'all')     return true
  if (f === 'running') return task.status === 'running'
  if (f === 'pending') return task.status === 'pending'
  if (f === 'done')    return task.status === 'done'
  return task.status === 'failed'
}

function matchesType(task: Task, f: TypeFilter): boolean {
  if (f === 'all')     return true
  if (f === 'publish') return PUBLISH_TYPES.has(task.type)
  if (f === 'upload')  return task.type === 'upload'
  if (f === 'license') return LICENSE_TYPES.has(task.type)
  if (f === 'status')  return STATUS_TYPES.has(task.type)
  return CHANGE_TYPES.has(task.type)
}

const isStatusTask = (task: Task): boolean => STATUS_TYPES.has(task.type)

// Free-text match across the row's visible identity: the content name, who
// published it, and any target screen name — so an operator can find "that
// thing I sent to Lobby TV" by typing content, person, or screen.
function matchesSearch(task: Task, q: string): boolean {
  if (!q) return true
  const hay = [
    task.target_label,
    task.actor_name ?? '',
    ...task.screens.map(s => s.screen_name ?? ''),
  ].join(' ').toLowerCase()
  return hay.includes(q)
}

// Branch scope. 'all' passes everything; otherwise the task must target at
// least one screen in that branch (looked up via the screen→branch map).
// Screen-less activity (uploads/changes) isn't branch-bound, so it only ever
// shows under 'all' — filtering by a branch shouldn't surface an unrelated
// upload.
function matchesBranch(task: Task, branchId: string, screenBranch: Record<number, number | undefined>): boolean {
  if (branchId === 'all') return true
  return task.screens.some(s => screenBranch[s.screen_id]?.toString() === branchId)
}

export default function ActivityCenterPage() {
  const { t, i18n } = useTranslation()
  const { tasks, runningTasks, clearFinished } = useTaskCenter()

  const { branches } = useBranchFilter()

  const [status, setStatus] = useState<StatusFilter>('all')
  const [type, setType]     = useState<TypeFilter>('all')
  // Connectivity (online/offline) tasks are logged but hidden by default — this
  // toggle surfaces them without switching the whole type filter.
  const [showStatus, setShowStatus] = useState(false)
  const [search, setSearch] = useState('')
  const [branch, setBranch] = useState('all')
  const [page, setPage]     = useState(1)
  const [clearOpen, setClearOpen] = useState(false)
  // Clear erases the whole org's history, so the server allows org admins only.
  const { fullAccess: canClear } = usePermissions()
  // Phone: the filter row doesn't fit, so filters live in a sheet and
  // Export / Clear behind a "more" menu (desktop keeps the inline row).
  const isMobile = useIsMobile()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const activeFilterCount = (branch !== 'all' ? 1 : 0) + (type !== 'all' ? 1 : 0) + (showStatus ? 1 : 0)
  const [clearing, setClearing]   = useState(false)
  const [exporting, setExporting] = useState(false)

  // Screen→branch map, so tasks (whose screen rows carry no branch) can be
  // filtered by branch. Fetched once; the feed is small so a stale entry for a
  // just-reassigned screen self-heals on the next visit.
  const [screenBranch, setScreenBranch] = useState<Record<number, number | undefined>>({})
  useEffect(() => {
    getScreens()
      .then(r => {
        const map: Record<number, number | undefined> = {}
        for (const s of r.data as { id: number; branch_id?: number }[]) map[s.id] = s.branch_id
        setScreenBranch(map)
      })
      .catch(() => { /* branch filter just stays empty-match until it loads */ })
  }, [])

  usePageHeader({
    title: t('activity.title', 'Activity Center'),
    subtitle: t('activity.subtitle', 'Publishing, uploads and changes across your screens'),
  })

  const q = search.trim().toLowerCase()
  // Ids of the in-flight tasks already rendered in the "Running now" panel
  // (running/pending publishes). They're excluded from the feed below so each
  // task lives in exactly ONE place: while in flight it's the live card in the
  // panel, and the moment it completes it moves into the feed as a single
  // Completed row — never a running card AND a duplicate done row.
  const runningIds = useMemo(
    () => new Set(runningTasks.filter(rt => !isUploadTask(rt)).map(rt => (rt as Task).id)),
    [runningTasks],
  )
  const filtered = useMemo(
    () => tasks.filter(task =>
      !runningIds.has(task.id)
      // Connectivity churn (screen online/offline) is hidden unless the toggle
      // is on or the operator explicitly filters to the Status bucket.
      && (showStatus || type === 'status' || !isStatusTask(task))
      && matchesStatus(task, status)
      && matchesType(task, type)
      && matchesSearch(task, q)
      && matchesBranch(task, branch, screenBranch),
    ),
    [tasks, runningIds, showStatus, status, type, q, branch, screenBranch],
  )

  // Reset to the first page whenever the filter narrows/changes the list, so
  // the operator isn't stranded on an out-of-range page.
  useEffect(() => { setPage(1) }, [status, type, q, branch, showStatus])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const curPage    = Math.min(page, totalPages)
  const paged      = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE)

  // Only finished (done/failed) rows can be cleared; in-flight work is kept.
  const finishedCount = tasks.filter(t => t.status === 'done' || t.status === 'failed').length

  const handleClear = async () => {
    setClearing(true)
    try {
      const removed = await clearFinished()
      toast.success(t('activity.cleared', { defaultValue: 'Cleared {{count}} items', count: removed }))
    } catch {
      toast.error(t('activity.clearFailed', 'Failed to clear activity'))
    } finally {
      setClearing(false)
      setClearOpen(false)
    }
  }

  // Server-side CSV of the full history under the current filter (the feed
  // itself is capped in memory, so we can't just serialize `filtered`).
  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await exportTasks({
        ...(status !== 'all' ? { status } : {}),
        ...(type !== 'all' ? { type } : {}),
        ...(q ? { q } : {}),
        ...(branch !== 'all' ? { branch_id: Number(branch) } : {}),
        // Match the feed: status churn only when the toggle (or the Status
        // bucket) shows it — the export used to include it regardless.
        include_status: showStatus || type === 'status',
      })
      const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'text/csv;charset=utf-8;' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `activity-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t('activity.exportFailed', 'Failed to export activity'))
    } finally {
      setExporting(false)
    }
  }

  const statusTabs: { key: StatusFilter; label: string }[] = [
    { key: 'all',     label: t('tasks.filter.all', 'All') },
    { key: 'running', label: t('tasks.filter.running', 'Running') },
    { key: 'pending', label: t('tasks.filter.pending', 'Pending') },
    { key: 'done',    label: t('tasks.filter.done', 'Completed') },
    { key: 'failed',  label: t('tasks.filter.failed', 'Failed') },
  ]

  const typeLabels: Record<TypeFilter, string> = {
    all:     t('tasks.filter.allTypes', 'All types'),
    publish: t('tasks.filter.publishes', 'Publishes'),
    upload:  t('tasks.filter.uploads', 'Uploads'),
    change:  t('tasks.filter.changes', 'Changes'),
    license: t('tasks.filter.license', 'License'),
    status:  t('tasks.filter.status', 'Status changes'),
  }

  // The running-now panel is a live progress view; show it only for the
  // in-flight filters so it doesn't contradict a Completed/Failed-scoped list.
  const showRunning = runningTasks.length > 0 && (status === 'all' || status === 'running' || status === 'pending')

  const Prev = ChevronLeft
  const Next = ChevronRight

  // Day heading for the feed dividers — Today / Yesterday / localized date.
  const dayLabel = (iso: string) => {
    const d = new Date(iso)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    if (d.toDateString() === today.toDateString()) return t('common.today', 'Today')
    if (d.toDateString() === yesterday.toDateString()) return t('common.yesterday', 'Yesterday')
    return d.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-GB', {
      weekday: 'long', day: 'numeric', month: 'short',
    })
  }

  return (
    // Plain flowing column (the panel scrolls when the feed is long). We do NOT
    // use `flex-1 min-h-0` here: the shell's content wrapper isn't a bounded
    // flex parent, so a `flex: 1 1 0%` feed would collapse to ~0 height and hide
    // the list. Flowing keeps every row visible and the controls readable.
    // Full width — fills the content panel rather than a narrow centered column.
    <div className="space-y-4 w-full">
      {/* Running-now panel — the live progress bars at the top of the page. */}
      {showRunning && (
        <div className="rounded-2xl border border-primary/30 bg-muted p-4">
          <div className="flex items-center gap-2 mb-3">
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
            <h2 className="text-sm font-semibold">{t('tasks.running', 'Waiting for screens')}</h2>
            <span className="text-xs text-muted-foreground">({runningTasks.length})</span>
          </div>
          <div className="space-y-3">
            {runningTasks.map(rt => (
              <RunningRow key={isUploadTask(rt) ? rt.id : `pub-${rt.id}`} task={rt} />
            ))}
          </div>
        </div>
      )}

      {/* Search — matches content name, publisher, or target screen. */}
      <div className="relative w-full sm:max-w-sm">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('activity.search', 'Search content, person or screen…')}
          className="ps-9 pe-9 h-9"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label={t('common.clearSearch', 'Clear search')}
            className="absolute end-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Filter bar — status segmented control (left) + branch/type dropdowns
          & Clear (right). */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center rounded-lg bg-card p-0.5 max-w-full overflow-x-auto no-scrollbar">
          {statusTabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatus(tab.key)}
              className={cn(
                'shrink-0 px-3 py-1.5 max-md:min-h-9 text-xs font-medium rounded-md transition-colors',
                status === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isMobile ? (
          <div className="flex w-full items-center gap-2">
            <Button variant="outline" className="h-10 flex-1 gap-2" onClick={() => setFiltersOpen(true)}>
              <Filter className="w-4 h-4" />
              {t('activity.filters', 'Filters')}
              {activeFilterCount > 0 && <span className="tabular-nums">({activeFilterCount})</span>}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={t('common.more', 'More')}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card"
              >
                <MoreHorizontal className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem
                  disabled={tasks.length === 0 || exporting}
                  onClick={handleExport}
                  className="gap-2 min-h-10"
                >
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {t('activity.export', 'Export')}
                </DropdownMenuItem>
                {canClear && (
                  <DropdownMenuItem
                    disabled={finishedCount === 0 || clearing}
                    onClick={() => setClearOpen(true)}
                    className="gap-2 min-h-10 text-destructive focus:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('activity.clear', 'Clear')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
        <div className="flex items-center gap-2">
          {/* Branch scope — only worth showing to multi-branch orgs. */}
          {branches.length > 1 && (
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="h-8 gap-1.5 text-xs w-auto min-w-[8rem] bg-card">
                <MapPin className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <SelectValue>
                  {branch === 'all'
                    ? t('screens.allBranches', 'All Branches')
                    : branches.find(b => b.id.toString() === branch)?.name ?? t('screens.allBranches', 'All Branches')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('screens.allBranches', 'All Branches')}</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id.toString()}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-2 h-8 px-3 rounded-lg border border-border bg-card text-xs font-medium text-foreground hover:bg-muted transition-colors">
              <Filter className="w-3.5 h-3.5" />
              {typeLabels[type]}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{t('tasks.filter.type', 'Type')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={type} onValueChange={v => setType(v as TypeFilter)}>
                {(Object.keys(typeLabels) as TypeFilter[]).map(k => (
                  <DropdownMenuRadioItem key={k} value={k}>{typeLabels[k]}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Show/hide the high-churn screen online/offline activity. */}
          <button
            type="button"
            onClick={() => setShowStatus(v => !v)}
            aria-pressed={showStatus}
            title={t('tasks.filter.showStatusHint', 'Include screen online/offline changes in the feed')}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-medium transition-colors ${
              showStatus ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:bg-muted'
            }`}
          >
            {showStatus ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {t('tasks.filter.showStatus', 'Status changes')}
          </button>

          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            disabled={tasks.length === 0 || exporting}
            onClick={handleExport}
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {t('activity.export', 'Export')}
          </Button>

          {canClear && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              disabled={finishedCount === 0 || clearing}
              onClick={() => setClearOpen(true)}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('activity.clear', 'Clear')}
            </Button>
          )}
        </div>
        )}
      </div>

      {/* Phone filters sheet — the same three filters as the desktop row. */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[max(1rem,env(safe-area-inset-bottom))]">
          <SheetHeader>
            <SheetTitle>{t('activity.filters', 'Filters')}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-5">
            {branches.length > 1 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">{t('screens.branch', 'Branch')}</p>
                <Select value={branch} onValueChange={setBranch}>
                  <SelectTrigger className="h-11">
                    <SelectValue>
                      {branch === 'all'
                        ? t('screens.allBranches', 'All Branches')
                        : branches.find(b => b.id.toString() === branch)?.name ?? t('screens.allBranches', 'All Branches')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('screens.allBranches', 'All Branches')}</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id.toString()}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">{t('tasks.filter.type', 'Type')}</p>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(typeLabels) as TypeFilter[]).map(k => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setType(k)}
                    className={cn(
                      'flex min-h-11 items-center justify-between gap-2 rounded-lg border px-3 text-sm',
                      type === k ? 'border-primary bg-primary/10 text-primary' : 'border-border',
                    )}
                  >
                    {typeLabels[k]}
                    {type === k && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowStatus(v => !v)}
              aria-pressed={showStatus}
              className={cn(
                'flex min-h-11 w-full items-center gap-2 rounded-lg border px-3 text-sm',
                showStatus ? 'border-primary bg-primary/10 text-primary' : 'border-border',
              )}
            >
              {showStatus ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              {t('tasks.filter.showStatus', 'Status changes')}
            </button>
            <Button className="h-11 w-full" onClick={() => setFiltersOpen(false)}>
              {t('common.done', 'Done')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <div>
        {filtered.length === 0 ? (
          <EmptyState
            icon={Inbox}
            iconClassName="text-muted-foreground"
            title={tasks.length === 0 ? t('activity.emptyTitle', 'No activity yet') : t('tasks.noMatchTitle', 'No matching activity')}
            description={tasks.length === 0
              ? t('activity.empty', 'No activity yet. Publishing, uploads and changes will show up here.')
              : t('tasks.noMatch', 'No activity matches the current filter.')}
          />
        ) : (
          <>
            <div className="space-y-2">
              {/* Day dividers — inserted whenever the (newest-first) feed
                  crosses into an earlier day within the current page. */}
              {paged.map((task, i) => {
                const day = dayLabel(task.created_at)
                const prevDay = i > 0 ? dayLabel(paged[i - 1].created_at) : null
                return (
                  <Fragment key={task.id}>
                    {day !== prevDay && (
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pt-2 first:pt-0">
                        {day}
                      </p>
                    )}
                    <TaskRow task={task} />
                  </Fragment>
                )
              })}
            </div>

            {/* Pager — only shown once the feed spills past one page. */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground tabular-nums">
                  {t('dashboard.showingRange', {
                    defaultValue: 'Showing {{from}}–{{to}} of {{total}}',
                    from: (curPage - 1) * PAGE_SIZE + 1,
                    to: Math.min(curPage * PAGE_SIZE, filtered.length),
                    total: filtered.length,
                  })}
                </p>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={curPage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    aria-label={t('common.previous', 'Previous')}
                  >
                    <Prev className="w-4 h-4" />
                  </Button>
                  <span className="text-[11px] tabular-nums text-muted-foreground px-1 min-w-[3rem] text-center">
                    {curPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={curPage >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    aria-label={t('common.next', 'Next')}
                  >
                    <Next className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={clearOpen}
        title={t('activity.clearTitle', 'Clear finished activity')}
        description={t('activity.clearDesc', {
          defaultValue: 'This removes {{count}} completed and failed items. In-progress work is kept.',
          count: finishedCount,
        })}
        confirmLabel={t('activity.clear', 'Clear')}
        onConfirm={handleClear}
        onClose={() => setClearOpen(false)}
      />
    </div>
  )
}
